import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useCookies } from "react-cookie";
import axiosInstance from "../services/axios";
import { useAuth } from "../hooks/useAuth";
import { useToast } from "@chakra-ui/react";

const CartContext = createContext();

// Custom hook to use the cart context
export const useCart = () => {
  return useContext(CartContext);
};

// CartProvider component to wrap around components that need access to the cart context
export const CartProvider = ({ children }) => {
  const [cookies, setCookie] = useCookies(["cart"]);
  const [cartItems, setCartItems] = useState(cookies.cart || []);
  const {user} = useAuth();
  const toast = useToast();

  // Update the cart cookie whenever cartItems changes
  useEffect(() => {
    setCookie("cart", cartItems, { path: "/" });
  }, [cartItems, setCookie]);

  // Function to add an item to the cart
  const addToCart = useCallback((item) => {
    if(user && item.publisher.id === user.user_id){
      toast({
        title: 'You cannot add your own track to basket',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    const existingItemIndex = cartItems.findIndex((cartItem) => cartItem.track_id === item.track_id);
    if (existingItemIndex !== -1) {
      // If item already exists, increase its quantity
      const updatedCart = [...cartItems];
      updatedCart[existingItemIndex].quantity += 1;
      setCartItems(updatedCart);
    } else {
      // If item doesn't exist, add it to cart with quantity 1
      setCartItems([...cartItems, { ...item, quantity: 1 }]);
    }
  }, [user, cartItems, toast]);

  // Function to remove an item from the cart by its index
  const removeFromCart = useCallback((index) => {
    setCartItems(prev => prev.filter((_, i) => i !== index));
  }, []);

  // Function to decrease the quantity of an item in the cart
  const decreaseQuantity = useCallback((index) => {
    setCartItems(prev => {
      const updatedCart = [...prev];
      if (updatedCart[index].quantity > 1) {
        updatedCart[index].quantity -= 1;
        return updatedCart;
      } else {
        return prev.filter((_, i) => i !== index);
      }
    });
  }, []);

  // Function to increase the quantity of an item in the cart
  const increaseQuantity = useCallback((index) => {
    setCartItems(prev => {
      const updatedCart = [...prev];
      updatedCart[index].quantity += 1;
      return updatedCart;
    });
  }, []);


  const updateCart = useCallback(async () => {
    setCartItems(prevItems => {
      if (prevItems.length === 0) return prevItems;
      
      // Fetch updated data for each item
      Promise.all(
        prevItems.map(async (item, index) => {
          try {
            const res = await axiosInstance.post("/music/get_track_by_id/" + item.track_id);
            return {...res.data, quantity: prevItems[index].quantity};
          } catch (error) {
            console.error("Error updating cart item:", error);
            return item; // Return original item if update fails
          }
        })
      ).then(updatedItems => {
        setCartItems(updatedItems);
      });
      
      return prevItems; // Return current items while fetching
    });
  }, []);
  
  // Function to change the quantity of an item in the cart based on input value
  const changeQuantity = useCallback((index, newValue) => {
    setCartItems(prev => {
      const updatedCart = [...prev];
      if (newValue > 0) {
        updatedCart[index].quantity = newValue;
      } else {
        updatedCart[index].quantity = 0;
      }
      return updatedCart;
    });
  }, []);

  // Function to clear all items from the cart
  const clearCart = useCallback(() => {
    setCartItems([]);
  }, []);

  // Provide the cart context to children components
  return (
    <CartContext.Provider
      value={{
        cartItems,
        addToCart,
        removeFromCart,
        decreaseQuantity,
        increaseQuantity,
        clearCart,
        changeQuantity,
        updateCart
      }}
    >
      {children}
    </CartContext.Provider>
  );
};